/**
 * Adds a watermark to an image
 * @param {HTMLImageElement} image - The original image element
 * @param {Object} options - Watermark options
 * @param {string} options.position - The position of the watermark (topLeft, topRight, center, bottomLeft, bottomRight)
 * @param {number} options.opacity - The opacity of the watermark (0-1)
 * @param {number} options.size - The size of the watermark relative to the image (0-1)
 * @param {boolean} options.isCustomImage - Whether to use a custom image as watermark
 * @param {string} options.watermarkText - The text to use as watermark (if not using custom image)
 * @param {HTMLImageElement} options.watermarkImage - The image to use as watermark (if using custom image)
 * @param {HTMLCanvasElement} canvas - The canvas element to use
 * @returns {Promise<string>} - Promise resolving to the watermarked image data URL
 */
export const addWatermark = (image, options, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // Set canvas dimensions to match image
      canvas.width = image.width;
      canvas.height = image.height;
      
      // Get canvas context
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw original image
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      // Apply watermark
      if (options.isCustomImage && options.watermarkImage) {
        applyImageWatermark(ctx, options, canvas.width, canvas.height);
      } else if (options.watermarkText && options.watermarkText.trim()) {
        applyTextWatermark(ctx, options, canvas.width, canvas.height);
      }
      
      // Convert to data URL with better quality
      const dataUrl = canvas.toDataURL('image/png', 0.9);
      resolve(dataUrl);
    } catch (error) {
      console.error('Error in addWatermark:', error);
      reject(error);
    }
  });
};

/**
 * Applies a text watermark to the canvas
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} options - Watermark options
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
const applyTextWatermark = (ctx, options, width, height) => {
  const { watermarkText, opacity, size, position } = options;
  
  // Save current context state
  ctx.save();
  
  // Set text properties
  ctx.globalAlpha = opacity;
  const fontSize = Math.max(14, Math.round(Math.min(width, height) * size));
  ctx.font = `${fontSize}px Arial, sans-serif`;
  
  // Set text color with a slight gradient for better contrast
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
  gradient.addColorStop(1, 'rgba(220, 220, 220, 0.9)');
  ctx.fillStyle = gradient;
  
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // Add shadow for better visibility on any background
  ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  
  // Calculate position
  const textWidth = ctx.measureText(watermarkText).width;
  const textHeight = fontSize * 0.8; // Approximate height based on font size
  const pos = getPosition(position, width, height, textWidth, textHeight);
  
  // Draw text
  ctx.fillText(watermarkText, pos.x, pos.y);
  
  // Restore context
  ctx.restore();
};

/**
 * Applies an image watermark to the canvas
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} options - Watermark options
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
const applyImageWatermark = (ctx, options, width, height) => {
  const { watermarkImage, opacity, size, position } = options;
  
  // Save current context
  ctx.save();
  
  // Calculate watermark dimensions
  const watermarkWidth = watermarkImage.width;
  const watermarkHeight = watermarkImage.height;
  const aspectRatio = watermarkWidth / watermarkHeight;
  
  // Scale watermark based on size option
  const maxDimension = Math.min(width, height) * size;
  let scaledWidth, scaledHeight;
  
  if (watermarkWidth > watermarkHeight) {
    scaledWidth = maxDimension;
    scaledHeight = maxDimension / aspectRatio;
  } else {
    scaledHeight = maxDimension;
    scaledWidth = maxDimension * aspectRatio;
  }
  
  // Calculate position
  const pos = getPosition(position, width, height, scaledWidth, scaledHeight);
  
  // Draw watermark with opacity
  ctx.globalAlpha = opacity;
  ctx.drawImage(
    watermarkImage, 
    pos.x - scaledWidth / 2, 
    pos.y - scaledHeight / 2, 
    scaledWidth, 
    scaledHeight
  );
  
  // Restore context
  ctx.restore();
};

/**
 * Calculates position coordinates based on position option
 * @param {string} position - Position option
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} elementWidth - Width of the element to position
 * @param {number} elementHeight - Height of the element to position
 * @returns {Object} - Object with x and y coordinates
 */
const getPosition = (position, width, height, elementWidth, elementHeight) => {
  const padding = Math.min(20, Math.round(Math.min(width, height) * 0.05)); // Responsive padding
  
  switch (position) {
    case 'topLeft':
      return {
        x: padding + elementWidth / 2,
        y: padding + elementHeight / 2
      };
    case 'topRight':
      return {
        x: width - padding - elementWidth / 2,
        y: padding + elementHeight / 2
      };
    case 'bottomLeft':
      return {
        x: padding + elementWidth / 2,
        y: height - padding - elementHeight / 2
      };
    case 'bottomRight':
      return {
        x: width - padding - elementWidth / 2,
        y: height - padding - elementHeight / 2
      };
    case 'center':
    default:
      return {
        x: width / 2,
        y: height / 2
      };
  }
}; 